import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";

const purple = "#2E2868";


const ProvidersSubsection = ({
  subjectId,
  isCreate,
  form,
  providers,
  setProviders,
  isOpen,
  onToggle,
  saveSubject,
  getProviderTypes,
  loadSubject,
}) => {
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [editingProviderIndex, setEditingProviderIndex] = useState(null);
  const [providerTypes, setProviderTypes] = useState([]);

  const [newProvider, setNewProvider] = useState({
    id: null,
    name: "",
    provider_type: "",
    // notes: "", 
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      const types = await getProviderTypes();
      if (!mounted) return;
      setProviderTypes(types || []);
    })();
    return () => {
      mounted = false;
    };
  }, [getProviderTypes]);

  const handleProviderChange = (field, val) =>
    setNewProvider((prev) => ({ ...prev, [field]: val }));

  const resetForm = () => {
    setNewProvider({ id: null, name: "", provider_type: "", notes: "" });
    setEditingProviderIndex(null);
    setShowAddProvider(false);
  };

  const syncFromFreshSubject = (fresh) => {
    if (!fresh?.subject_physicians) return;

    setProviders(
      fresh.subject_physicians.map((p) => ({
        id: p.id,
        name: p.name ?? "",
        provider_type: p.provider_type ?? "",
        // notes: "", // backend doesn't return notes
      }))
    );
  };

  const saveProvider = async () => {
    if (!newProvider.name?.trim() || !newProvider.provider_type?.trim()) return;

    const payloadObject = {
      ...(newProvider.id && { id: newProvider.id }),
      name: newProvider.name.trim(),
      provider_type: newProvider.provider_type.trim(),
      //  DON'T send notes (not supported by subject_physicians_input)
    };

    const updatedList =
      editingProviderIndex !== null
        ? providers.map((p, idx) =>
            idx === editingProviderIndex ? { ...p, ...payloadObject, notes: newProvider.notes } : p
          )
        : [...providers, { ...payloadObject, notes: newProvider.notes }];

    // Create mode -> client-side only
    if (isCreate && form?.setValue) {
      setProviders(updatedList);
      form.setValue({
        key: "subject_physicians",
        value: updatedList.map((p) => ({
          name: p.name || null,
          provider_type: p.provider_type || null,
        })),
      });
      resetForm();
      return;
    }

    //  Backend -> whitelist only allowed fields
    await saveSubject({
      input: {
        subjects: { id: subjectId },
        subject_physicians: updatedList.map((p) => ({
          ...(p.id && { id: p.id }),
          name: p.name || null,
          provider_type: p.provider_type || null,
        })),
      },
    });

    //  Force reload
    const fresh = await loadSubject({ force: true });
    syncFromFreshSubject(fresh);

    resetForm();
  };

  const deleteCurrentProvider = async () => {
    if (isCreate || editingProviderIndex === null) return;

    const record = providers[editingProviderIndex];
    if (!record?.id) return;

    await saveSubject({
      input: { subjects: { id: subjectId } },
      input_delete: [{ id: record.id, key: "subject_physicians" }],
    });

    //  Force reload
    const fresh = await loadSubject({ force: true });
    syncFromFreshSubject(fresh);

    resetForm();
  };

  return (
    <div className="mb-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center" onClick={onToggle} style={{ cursor: "pointer" }}>
          <i
            className={`bi ${isOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
            style={{ color: purple, fontSize: "14px", marginRight: "6px" }}
          />
          <h6 className="fw-bold m-0" style={{ color: purple }}>
            Providers ({providers.length})
          </h6>
        </div>

        <span
          onClick={() => {
            setShowAddProvider(true);
            setEditingProviderIndex(null);
            setNewProvider({ id: null, name: "", provider_type: "", notes: "" });
          }}
          style={{ fontSize: "13px", color: purple, cursor: "pointer", fontWeight: 600 }}
        >
          + Add
        </span>
      </div>

      {/* FORM */}
      {showAddProvider && (
        <div className="p-3 mb-3 position-relative" style={{ background: "#f2f2f7", borderRadius: "12px" }}>
          <span
            onClick={() => setShowAddProvider(false)}
            style={{
              position: "absolute",
              right: "12px",
              top: "10px",
              cursor: "pointer",
              color: purple,
              fontWeight: "bold",
              fontSize: "23px",
            }}
          >
            ×
          </span>

          <div className="mb-2">
            <label className="mb-1">Provider Name</label>
            <input
              value={newProvider.name}
              onChange={(e) => handleProviderChange("name", e.target.value)}
              className="form-control rounded-pill border-0"
            />
          </div>

          <div className="mb-2">
            <label className="mb-1">Specialty</label>
            <select
              value={newProvider.provider_type}
              onChange={(e) => handleProviderChange("provider_type", e.target.value)}
              className="form-control rounded-pill border-0"
            >
              <option value="">Select provider type</option>
              {(providerTypes || []).map((pt) => (
                <option key={pt.id ?? pt.type} value={pt.type}>
                  {pt.type}
                </option>
              ))}
            </select>
          </div>

          {/* 
          <div className="mb-3">
            <label className="mb-1">Notes (UI only)</label>
            <textarea
              rows={2}
              value={newProvider.notes}
              onChange={(e) => handleProviderChange("notes", e.target.value)}
              className="form-control rounded-3 border-0"
            />
          </div> */}

          <div className="d-flex justify-content-center gap-3">
            <button
              className="btn btn-sm"
              onClick={saveProvider}
              style={{ background: purple, color: "#fff", borderRadius: "20px", padding: "4px 18px" }}
            >
              Save
            </button>

            {!isCreate && editingProviderIndex !== null && (
              <button
                className="btn btn-sm"
                onClick={deleteCurrentProvider}
                style={{ background: "#c0392b", color: "#fff", borderRadius: "20px", padding: "4px 18px" }}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      )}

      {/* LIST */}
      {isOpen &&
        providers.map((item, idx) => (
          <div
            key={item.id ?? idx}
            className="d-flex justify-content-between align-items-center mb-2"
            style={{ fontSize: "14px", gap: "8px" }}
          >
            <div>
              <div>
                {item.name}
                {item.provider_type && ` | ${item.provider_type}`}
              </div>
              <div style={{ fontSize: "12px", color: "#777" }}>{item.notes || "Provider details"}</div>
            </div>

            {!isCreate && (
              <FontAwesomeIcon
                icon={faPen}
                style={{ cursor: "pointer", color: purple, fontSize: "14px" }}
                onClick={() => {
                  setNewProvider({
                    id: item.id ?? null,
                    name: item.name ?? "",
                    provider_type: item.provider_type ?? "",
                    notes: item.notes ?? "",
                  });
                  setEditingProviderIndex(idx);
                  setShowAddProvider(true);
                }}
              />
            )}
          </div>
        ))}
    </div>
  );
};

export default ProvidersSubsection;